import { useState } from "react";
import { doc, setDoc, arrayUnion } from "firebase/firestore";
import { db, storage } from "config/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function LandingPage({ open, setOpen, t }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    corporation: "",
    email: "",
    phone: "",
    message: "",
  });

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) setFile(e.target.files[0]);
  };

  const validatePhone = (phone) => {
    const regex = /^(\+374|0)\d{8}$/;
    return regex.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.phone) {
      alert("Խնդրում ենք մուտքագրել հեռախոսահամար");
      return;
    }
    if (!validatePhone(formData.phone)) {
      alert(
        "Խնդրում ենք մուտքագրել վավեր հեռախոսահամար (օր.՝ 374XXXXXXXX կամ 0XXXXXXXX)",
      );
      return;
    }

    try {
      setUploading(true);
      let fileURL = null;

      if (file) {
        const storageRef = ref(
          storage,
          `feedback_files/${formData.phone}/${file.name}`,
        );
        await uploadBytes(storageRef, file);
        fileURL = await getDownloadURL(storageRef);
      }

      const feedbackRef = doc(db, "feedback", formData.phone);

      await setDoc(
        feedbackRef,
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          corporation: formData.corporation,
          email: formData.email,
          phone: formData.phone,
          messages: arrayUnion({
            text: formData.message,
            createdAt: new Date().toISOString(),
            file: fileURL || null,
            fileName: file ? file.name : null,
          }),
        },
        { merge: true },
      );

      alert("Հաղորդագրությունը հաջողությամբ ուղարկվեց");
      setOpen(false);
      setFormData({
        firstName: "",
        lastName: "",
        corporation: "",
        email: "",
        phone: "",
        message: "",
      });
      setFile(null);
    } catch (err) {
      console.error("Error saving feedback:", err);
      alert("Տեղի ունեցավ սխալ");
    } finally {
      setUploading(false);
    }
  };

  const isPhoneValidLength = formData.phone.replace(/\D/g, "").length >= 8;

  return (
    <div className="relative bg-gray-100">
      {open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 px-4 sm:px-6">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl p-6 sm:p-8 overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                Հետադարձ Կապ
              </h2>
              <button
                onClick={() => setOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-xl sm:text-2xl leading-none"
              >
                ✕
              </button>
            </div>

            <form className="space-y-4 sm:space-y-5" onSubmit={handleSubmit}>
              <div className="flex gap-4">
                {/* First Name */}
                <div className="w-full">
                  <label className="block text-sm font-medium mb-1">
                    Անուն<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg p-2 sm:p-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#D9A443]"
                    required
                  />
                </div>

                {/* Last Name */}
                <div className="w-full">
                  <label className="block text-sm font-medium mb-1">
                    Ազգանուն
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg p-2 sm:p-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#D9A443]"
                  />
                </div>
              </div>

              {/* Corporation / Profession */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Պաշտոն, Մասնագիտություն, Կազմակերպություն
                </label>
                <input
                  type="text"
                  name="corporation"
                  value={formData.corporation}
                  onChange={handleChange}
                  placeholder="Ուսուցիչ"
                  className="w-full border border-gray-300 rounded-lg p-2 sm:p-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#D9A443]"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Էլ․ հասցե
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg p-2 sm:p-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#D9A443]"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Հեռ․ համար<span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg p-2 sm:p-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#D9A443]"
                  placeholder="374XXXXXXXX կամ 0XXXXXXXX"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Հաղորդագրություն<span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full border border-gray-300 rounded-lg p-2 sm:p-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#D9A443]"
                  required
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Կցել ֆայլ (ըստ ցանկության)
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full border border-gray-300 rounded-lg p-2 sm:p-3 text-sm sm:text-base"
                />
                {file && (
                  <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">
                    Ընտրված ֆայլը՝ {file.name}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isPhoneValidLength || uploading}
                className={`w-full font-semibold py-2 sm:py-3 rounded-xl text-white text-sm sm:text-base transition-all ${
                  isPhoneValidLength && !uploading
                    ? "bg-[#D9A443] hover:bg-[#E8BB68]"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                {uploading ? "Վերբեռնում..." : "Ուղարկել"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
